import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AvatarIcon, CountryFlag, COUNTRY_NAME } from "@/lib/identity";
import { formatPrize } from "@/lib/prize";
import { PremiumBadge, PremiumCard } from "@/components/PremiumBadge";
import EditProfileDialog from "@/components/EditProfileDialog";
import VerificationCard from "@/components/VerificationCard";
import ShareCardModal from "@/components/ShareCardModal";
import { Crown, Trophy, PencilSimple, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = [
  { id: "account",  label: "Account" },
  { id: "awards",   label: "Awards" },
];

export default function Profile() {
  const { user, refresh } = useAuth();
  const params = useParams();
  const targetId = params.user_id;
  const [data, setData] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [season, setSeason] = useState(null);
  const [tab, setTab] = useState("account");

  useEffect(() => {
    const path = targetId ? `/season/profile/${targetId}` : "/season/profile/me";
    api.get(path).then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/season/current").then((r) => setSeason(r.data)).catch(() => {});
  }, [targetId]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const url = new URL(window.location.href);
    const flag = url.searchParams.get("nk_plus");
    const sessionId = url.searchParams.get("session_id");
    if (flag === "cancelled") {
      toast.message("NK+ upgrade cancelled");
      window.history.replaceState({}, "", "/profile");
      return;
    }
    if (flag !== "success" || !sessionId) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Welcome to NK+ 🎉");
          window.history.replaceState({}, "", "/profile");
          if (refresh) await refresh();
          const r = await api.get("/season/profile/me");
          setData(r.data);
          // Jump to account tab so they see their new perks
          setTab("account");
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout expired. Try again.");
          window.history.replaceState({}, "", "/profile");
          return;
        }
      } catch { /* keep polling */ }
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        toast.message("Still processing — refresh the page in a few seconds.");
        window.history.replaceState({}, "", "/profile");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return <div className="text-sm text-white/60" data-testid="profile-loading">Loading your profile…</div>;
  }
  const isMe = !targetId || data.user.user_id === user?.user_id;
  const s = data.stats || {};
  const level = data.level || { level: 1, label: "Participant" };

  return (
    <div className="max-w-4xl space-y-7" data-testid="profile-page">
      {/* HERO */}
      <header
        className="rounded-md p-5 sm:p-7 relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(11,61,46,0.65) 0%, rgba(16,21,24,0.92) 100%)",
          border: "1px solid rgba(212,175,55,0.25)",
        }}
        data-testid="profile-hero"
      >
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-nk-gold/8 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-4 sm:gap-6">
          <div className="shrink-0">
            <AvatarIcon id={data.user.avatar} size={84} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-nk-gold/80 font-medium mb-1.5">
              {isMe ? "Your profile" : "Player profile"}
            </p>
            <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white truncate flex items-center gap-2 flex-wrap">
              {data.user.username || data.user.name}
              <PremiumBadge active={data.user.nk_plus_active} size="md" showLabel />
            </h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2.5 text-xs sm:text-sm text-white/65">
              {data.user.country && (
                <span className="inline-flex items-center gap-1.5">
                  <CountryFlag country={data.user.country} size={13} />
                  {COUNTRY_NAME[data.user.country] || data.user.country}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Crown size={13} weight={level.level >= 5 ? "fill" : "regular"}
                       className={level.level >= 5 ? "text-nk-gold" : "text-white/45"} />
                Level {level.level} · {level.label}
              </span>
            </div>
          </div>
          {isMe && (
            <div className="flex flex-col gap-2 shrink-0 self-start">
              <button
                onClick={() => setShowEdit(true)}
                data-testid="edit-profile-btn"
                className="nk-btn nk-btn-ghost text-xs px-3 py-1.5"
              >
                <PencilSimple size={12} /> Edit
              </button>
              <button
                onClick={() => setShowShare(true)}
                data-testid="profile-share-btn"
                className="nk-btn nk-btn-ghost text-xs px-3 py-1.5 border-nk-gold/60 text-nk-gold hover:bg-nk-gold/10"
              >
                <ShareNetwork size={12} /> Share NK Card
              </button>
            </div>
          )}
        </div>

        {/* Stat strip inside hero */}
        <div className="relative mt-5 sm:mt-6 grid grid-cols-3 gap-3 sm:gap-5 border-t border-white/8 pt-4">
          <HeroStat label="Rating" value={s.knowledge_rating ?? 1000} hint={s.rating_peak ? `peak ${s.rating_peak}` : "lifetime"} />
          <HeroStat label="Season" value={(s.season_score ?? 0).toLocaleString()} hint="this season" highlight />
          <HeroStat label="Rank" value={data.season_rank ? `#${data.season_rank}` : "—"} hint={data.user.country || "unranked"} />
        </div>
      </header>

      {showEdit && (
        <EditProfileDialog
          user={data.user}
          onClose={() => setShowEdit(false)}
          onSaved={(updated) => {
            setData((d) => d ? { ...d, user: { ...d.user, ...updated } } : d);
            if (refresh) refresh();
            setShowEdit(false);
          }}
        />
      )}

      <ShareCardModal
        open={showShare}
        onClose={() => setShowShare(false)}
        profile={data}
        seasonNumber={season?.season_number}
        variant={season?.state === "NATIONAL_FREEZE" ? "season" : "always"}
      />


      <ShareCardModal
        open={showShare}
        onClose={() => setShowShare(false)}
        profile={data}
        seasonNumber={season?.season_number}
        variant={season?.state === "NATIONAL_FREEZE" ? "season" : "always"}
      />

      {/* Tab bar */}
      <div className="flex items-center gap-1 overflow-x-auto no-scrollbar" role="tablist" data-testid="profile-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            data-testid={`profile-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`shrink-0 px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all border ${
              tab === t.id
                ? "bg-nk-green text-white border-nk-gold"
                : "border-white/10 text-white/55 hover:text-white hover:border-white/20"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW tab moved to /home — PlayerOverview component embedded in Home.jsx */}

      {/* ACCOUNT */}
      {tab === "account" && isMe && (
        <div className="space-y-6" data-testid="profile-account">
          <PremiumCard active={!!user?.nk_plus_active} />
          <VerificationCard user={data.user} />
          <PrizeClaim />
          <PayoutDetails />
        </div>
      )}
      {tab === "account" && !isMe && (
        <div className="text-sm text-white/55 nk-card p-6 text-center">Only the player themselves can see account settings.</div>
      )}

      {/* AWARDS */}
      {tab === "awards" && (
        <div className="space-y-4" data-testid="profile-awards">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium">Hall of Fame</p>
          {(() => {
            const entries = Array.isArray(s.hall_of_fame_entries) ? s.hall_of_fame_entries : [];
            if (entries.length === 0) {
              return (
                <div className="nk-card p-8 text-center">
                  <Trophy size={28} weight="duotone" className="mx-auto text-nk-gold/55 mb-3" />
                  <p className="text-sm text-white/70">No Hall of Fame entries yet.</p>
                  <p className="text-xs text-white/45 mt-1">Finish #1 in your country at season end to be immortalized.</p>
                </div>
              );
            }
            return (
              <div className="space-y-2">
                {entries.map((e, i) => (
                  <div key={i} className="nk-card p-4 flex items-center gap-3">
                    <Crown size={18} weight="fill" className="text-nk-gold shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {e.title || `Season ${e.season_number} Champion`}
                      </p>
                      <p className="text-[11px] text-white/55">{e.country || ""} · {e.year || ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function HeroStat({ label, value, hint, highlight = false }) {
  return (
    <div className="border-l border-white/10 pl-3 first:border-l-0 first:pl-0">
      <p className="text-[10px] uppercase tracking-[0.16em] text-white/45 font-medium">{label}</p>
      <p className={`font-display text-xl sm:text-2xl font-medium mt-1 tabular-nums truncate ${highlight ? "text-nk-gold" : "text-white"}`}>
        {value}
      </p>
      <p className="text-[10px] text-white/40 mt-0.5 truncate">{hint}</p>
    </div>
  );
}

function PrizeClaim() {
  const [info, setInfo] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [method, setMethod] = useState("mobile_money");
  const [account, setAccount] = useState("");
  const [holder, setHolder] = useState("");
  const [bankName, setBankName] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/payouts/eligibility")
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null));
  };
  useEffect(() => { load(); }, []);

  if (!info) return null;
  if (!info.eligible && !info.existing_claim) {
    // Hide entirely for non-eligible players to keep the profile clean
    return null;
  }
  const claim = info.existing_claim;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/payouts/claim", {
        payout_method: method,
        payout_account: account.trim(),
        payout_account_name: holder.trim(),
        bank_name: method === "bank_transfer" ? bankName.trim() : null,
        notes: notes.trim() || null,
      });
      toast.success("Prize claim submitted — admin will review.");
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="prize-claim-section">
      <p className="eyebrow mb-3 text-nk-gold">Champion Prize</p>
      <div className="nk-card p-6 border-l-2 border-nk-gold">
        {claim ? (
          <ClaimStatus claim={claim} />
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
              <div>
                <h3 className="font-display text-2xl font-medium text-nk-gold flex items-center gap-2">
                  <Crown size={22} weight="fill" />
                  You finished #{info.rank} in {info.country}
                </h3>
                <p className="text-sm text-white/60 mt-1">
                  Season {info.season_number} prize: <span className="text-white font-medium font-mono">{formatPrize(info.prize, info.country)}</span>
                  {!info.season_closed && <span className="ml-2 text-[11px] text-white/45">(provisional — season not yet closed)</span>}
                </p>
              </div>
              {!formOpen && (
                <button onClick={() => setFormOpen(true)} data-testid="claim-prize-btn"
                        className="nk-btn nk-btn-gold">
                  Claim my prize →
                </button>
              )}
            </div>
            {formOpen && (
              <form onSubmit={submit} className="mt-4 space-y-3 max-w-xl">
                <div>
                  <label className="text-[11px] text-white/60 uppercase tracking-wider">Payout method</label>
                  <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="claim-method"
                          className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none">
                    <option value="mobile_money">Mobile Money</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>
                <LabeledInput label="Account holder name (matches ID)" value={holder} onChange={setHolder} testId="claim-holder" />
                <LabeledInput
                  label={method === "mobile_money" ? "Mobile money number" : "Bank account number"}
                  value={account} onChange={setAccount} testId="claim-account"
                />
                {method === "bank_transfer" && (
                  <LabeledInput label="Bank name" value={bankName} onChange={setBankName} testId="claim-bank" />
                )}
                <div>
                  <label className="text-[11px] text-white/60 uppercase tracking-wider">Notes (optional)</label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={280}
                            data-testid="claim-notes"
                            className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none" />
                </div>
                <div className="flex gap-2 pt-1">
                  <button type="submit" disabled={busy} data-testid="claim-submit-btn"
                          className="nk-btn nk-btn-gold disabled:opacity-40">
                    {busy ? "Submitting…" : "Submit claim"}
                  </button>
                  <button type="button" onClick={() => setFormOpen(false)}
                          className="px-4 py-2 border border-white/10 text-sm text-white/60 rounded-sm">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function LabeledInput({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[11px] text-white/60 uppercase tracking-wider">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} required minLength={2} maxLength={120}
             data-testid={testId}
             className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none" />
    </div>
  );
}

function ClaimStatus({ claim }) {
  const colorMap = {
    pending: "text-nk-gold", approved: "text-nk-gold",
    paid: "text-nk-gold", rejected: "text-nk-bad",
  };
  return (
    <div data-testid="claim-status">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] text-white/60 uppercase tracking-wider">Status</p>
          <p className={`font-display text-2xl font-medium capitalize ${colorMap[claim.status] || "text-white"}`}>
            {claim.status}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[11px] text-white/60 uppercase tracking-wider">Prize</p>
          <p className="font-mono text-lg text-nk-gold">{formatPrize({amount: claim.prize_amount, currency: claim.prize_currency}, claim.country)}</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <KV k="Country rank" v={`#${claim.rank} (${claim.country})`} />
        <KV k="Season" v={claim.season_number} />
        <KV k="Payout method" v={claim.payout_method?.replace("_", " ")} />
        <KV k="Submitted" v={new Date(claim.created_at).toLocaleString()} />
        {claim.status === "paid" && claim.payout_reference && (
          <KV k="Reference" v={claim.payout_reference} />
        )}
        {claim.status === "rejected" && claim.rejected_at && (
          <KV k="Rejected" v={new Date(claim.rejected_at).toLocaleString()} />
        )}
      </div>
      <p className="text-[11px] text-white/45 mt-4">
        Updates will appear here as our team reviews and processes your claim.
      </p>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <p className="text-[10px] text-white/60 uppercase tracking-wider">{k}</p>
      <p className="text-white break-words">{v}</p>
    </div>
  );
}

function PayoutDetails() {
  const [details, setDetails] = useState(null);
  const [editing, setEditing] = useState(false);
  const [method, setMethod] = useState("momo");
  const [holder, setHolder] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [momoProvider, setMomoProvider] = useState("");
  const [momoPhone, setMomoPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/profile/payout-details").then((r) => {
      const d = r.data?.payout_details;
      setDetails(d || null);
      if (d) {
        setMethod(d.method); setHolder(d.account_holder || "");
        setBankName(d.bank_name || ""); setAccountNumber(d.account_number || "");
        setMomoProvider(d.momo_provider || ""); setMomoPhone(d.momo_phone || "");
      }
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { method, account_holder: holder };
      if (method === "bank") { payload.bank_name = bankName; payload.account_number = accountNumber; }
      else { payload.momo_provider = momoProvider; payload.momo_phone = momoPhone; }
      await api.put("/profile/payout-details", payload);
      setDetails(payload);
      setEditing(false);
      toast.success("Prize-claim details saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <div className="flex items-baseline justify-between mb-4">
        <p className="eyebrow">Prize-claim details</p>
        {details && !editing && (
          <button data-testid="payout-edit-btn" onClick={() => setEditing(true)} className="text-xs text-nk-gold hover:underline">Edit</button>
        )}
      </div>
      <div className="nk-card p-5 sm:p-6 space-y-4">
        <p className="text-xs text-white/60 leading-relaxed">
          Only used if you become a National or International Champion. NKognito disburses prize money
          directly to the account you provide. We never store funds on your behalf and never charge entry fees.
        </p>
        {!editing && details && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Field k="Method" v={details.method === "bank" ? "Bank account" : "Mobile money"} />
            <Field k="Account holder" v={details.account_holder} />
            {details.method === "bank" ? (
              <>
                <Field k="Bank" v={details.bank_name} />
                <Field k="Account #" v={details.account_number} mono />
              </>
            ) : (
              <>
                <Field k="Provider" v={details.momo_provider} />
                <Field k="Phone" v={details.momo_phone} mono />
              </>
            )}
          </div>
        )}
        {!details && !editing && (
          <button data-testid="payout-add-btn" onClick={() => setEditing(true)} className="text-sm text-nk-gold hover:underline">
            + Add prize-claim details
          </button>
        )}
        {editing && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <MethodPill active={method === "momo"} onClick={() => setMethod("momo")}>Mobile money</MethodPill>
              <MethodPill active={method === "bank"} onClick={() => setMethod("bank")}>Bank account</MethodPill>
            </div>
            <TextInput testid="payout-holder" label="Account holder name" value={holder} onChange={setHolder} placeholder="As it appears on the account" />
            {method === "bank" ? (
              <>
                <TextInput testid="payout-bank" label="Bank name" value={bankName} onChange={setBankName} placeholder="e.g. GTBank, Ecobank Ghana" />
                <TextInput testid="payout-account" label="Account number" value={accountNumber} onChange={setAccountNumber} placeholder="0123456789" mono />
              </>
            ) : (
              <>
                <TextInput testid="payout-provider" label="Provider" value={momoProvider} onChange={setMomoProvider} placeholder="MTN MoMo, AirtelTigo, Vodafone Cash, Opay" />
                <TextInput testid="payout-phone" label="Phone number" value={momoPhone} onChange={setMomoPhone} placeholder="+233 / +234…" mono />
              </>
            )}
            <div className="flex gap-2 pt-2">
              <button
                data-testid="payout-save-btn"
                onClick={save}
                disabled={busy}
                className="nk-btn nk-btn-gold disabled:opacity-40"
              >{busy ? "Saving…" : "Save"}</button>
              <button onClick={() => setEditing(false)} className="px-4 py-2 border border-white/10 text-sm text-white/60 rounded-sm hover:text-white">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Field({ k, v, mono = false }) {
  return (
    <div>
      <p className="text-[11px] text-white/60 mb-0.5 uppercase tracking-wider">{k}</p>
      <p className={mono ? "font-mono text-sm" : "text-sm"}>{v || "—"}</p>
    </div>
  );
}

function MethodPill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${
        active ? "border-nk-gold bg-nk-gold/10 text-nk-gold" : "border-white/10 text-white/60 hover:text-white"
      }`}
    >{children}</button>
  );
}

function TextInput({ testid, label, value, onChange, placeholder, mono = false }) {
  return (
    <label className="block">
      <p className="text-[11px] text-white/60 mb-1 uppercase tracking-wider">{label}</p>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none transition-colors ${mono ? "font-mono" : ""}`}
      />
    </label>
  );
}
